import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {TenantService} from '../tenant.service';

const TOKEN_KEY = 'auth_token';

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
  confirmPassword: string;
}

export interface LoginRequest {
  email: string;
  password: string;
  remember?: boolean;
}

export interface LoginResponse {
  token?: string;
  email?: string;
  message?: string;
}

export interface VerifyOtpRequest {
  email: string;
  otp: string;
  remember?: boolean;
}

export interface VerifyOtpResponse {
  token: string;
  message: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
  confirmPassword: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);

  private _token = signal<string | null>(this.readToken());

  token = computed(() => this._token());

  isAuthenticated = computed(() => {
    const t = this._token();
    return !!t && !this.isTokenExpired(t);
  });

  role = computed(() => {
    const payload = this.decodePayload(this._token());
    return ((payload?.['ruolo'] as string) ?? '').toUpperCase();
  });

  isAdmin = computed(() =>
    this.role() === 'ADMIN' || this.role() === 'SUPERADMIN'
  );

  denominazione = computed(() => {
    const payload = this.decodePayload(this._token());
    return ((payload?.['denominazione'] as string) ?? '').trim();
  });

  email = computed(() => {
    const payload = this.decodePayload(this._token());
    return ((payload?.['email'] as string) ?? '').trim();
  });

  initials = computed(() => {
    const payload = this.decodePayload(this._token());
    const denominazione = ((payload?.['denominazione'] as string) ?? '').trim();

    if (!denominazione) return '';

    const parts = denominazione
      .split(/\s+/)
      .filter((p: string) => p.length > 0);

    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }

    return (parts[0][0] + parts[1][0]).toUpperCase();
  });

  constructor() {
    setInterval(() => {
      const t = this._token();

      if (t && this.isTokenExpired(t)) {
        this.logout();
        this.router.navigate(['/login']);
      }
    }, 30_000);
  }
  private tenantService = inject(TenantService);

  login(body: LoginRequest): Observable<LoginResponse> {
    const url = `${environment.apiBaseUrl || ''}/${this.tenantService.tenant}/dipendenti/login`;

    return this.http.post<LoginResponse>(url, body).pipe(
      tap(res => {
        if (res.token) {
          this.saveToken(res.token, body.remember ?? false);
        }
      })
    );
  }

  verifyOtp(body: VerifyOtpRequest): Observable<VerifyOtpResponse> {
    const url = `${environment.apiBaseUrl || ''}/${this.tenantService.tenant}/dipendenti/verifyOtp`;

    return this.http.post<VerifyOtpResponse>(url, {
      email: body.email,
      otp: body.otp,
    }).pipe(
      tap(res => {
        this.saveToken(res.token, body.remember ?? false);
      })
    );
  }

  logout(): void {
    try {
      localStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(TOKEN_KEY);
    } catch {}

    this._token.set(null);
  }

  getToken(): string | null {
    return this.readToken();
  }

  private saveToken(token: string, remember: boolean): void {
    try {
      if (remember) {
        localStorage.setItem(TOKEN_KEY, token);
        sessionStorage.removeItem(TOKEN_KEY);
      } else {
        sessionStorage.setItem(TOKEN_KEY, token);
        localStorage.removeItem(TOKEN_KEY);
      }
    } catch {}

    this._token.set(token);
  }

  private readToken(): string | null {
    try {
      const sessionToken = sessionStorage.getItem(TOKEN_KEY);
      if (sessionToken) return sessionToken;

      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  }

  private decodePayload(token: string | null): Record<string, any> | null {
    if (!token) return null;

    try {
      const payload = token.split('.')[1];
      return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    } catch {
      return null;
    }
  }

  private isTokenExpired(token: string): boolean {
    const payload = this.decodePayload(token);

    if (!payload || typeof payload['exp'] !== 'number') {
      return false;
    }

    return Date.now() >= payload['exp'] * 1000;
  }

  forgotPassword(body: ForgotPasswordRequest): Observable<void> {
    const url = `/${this.tenantService.tenant}/dipendenti/forgotPassword`;
    return this.http.post<void>(url, body);
  }

  resetPassword(
    body: ResetPasswordRequest
  ): Observable<void> {

    const url =
      `/${this.tenantService.tenant}/dipendenti/resetPassword`;

    return this.http.post<void>(
      url,
      body
    );
  }
}
